"use client";

import Image from "next/image";

import playIcon from "@/assets/svgs/play.svg";
import { cn } from "@/lib/tailwind";
import {
  DetailedHTMLProps,
  ElementRef,
  HTMLAttributes,
  MouseEventHandler,
  ReactEventHandler,
  useRef,
  useState,
  VideoHTMLAttributes,
} from "react";
import { CLink } from "../customLink/CustomLink";
import LazyLoadedVideo from "../LazyLoadedVideo";
import VisibilitySensor from "../VisibilitySensor";
import PortableText from "../PortableText";
import { PortableTextBlock } from "next-sanity";

export interface IVideoCardProps {
  videoProps: DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>;
  containerProps?: Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    "children"
  >;
  controlsProps?: Partial<{ onClick: () => void; className: string }>;
  action?: {
    cta: React.ReactNode;
    title: React.ReactNode;
    titleClassName?: string;
    containerClassName?: string;
    description?: React.ReactNode;
    subDescription?: React.ReactNode;
  };
  showControls?: boolean;
  url?: string;
  onClick?: () => void;
  onVisible?: (isVisible: boolean) => void;
}

const VideoCard = ({
  action,
  containerProps = {},
  controlsProps,
  videoProps,
  showControls,
  url,
  onClick,
  onVisible,
}: IVideoCardProps) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef<ElementRef<"video">>(null);

  const handleTogglePlay: MouseEventHandler<HTMLDivElement> = (e) => {
    e?.stopPropagation();
    if (!videoRef?.current) return;

    const state = !isPlaying;
    setIsPlaying(state);

    if (state) videoRef?.current?.play();
    else videoRef?.current?.pause();
    controlsProps?.onClick?.();
  };

  const handleEnded: ReactEventHandler<HTMLVideoElement> = (e) => {
    if (videoRef?.current) videoRef.current.currentTime = 0;
    setIsPlaying(false);
    videoProps?.onEnded?.(e);
  };

  return (
    <div
      {...containerProps}
      {...(isPlaying && showControls && { onClick: handleTogglePlay })}
      className={cn("relative", containerProps?.className)}
      onClick={onClick}
    >
      {url && !showControls ? (
        <CLink href={url}>
          <LazyLoadedVideo
            width="100%"
            height="100%"
            videoRef={videoRef}
            {...videoProps}
            onEnded={handleEnded}
            className={cn(videoProps?.className)}
          />
        </CLink>
      ) : (
        <LazyLoadedVideo
          width="100%"
          height="100%"
          videoRef={videoRef}
          {...videoProps}
          onEnded={handleEnded}
          className={cn(videoProps?.className)}
        />
      )}

      {!isPlaying && showControls && (
        <Image
          width={73}
          height={90}
          src={playIcon?.src}
          onClick={handleTogglePlay}
          alt={isPlaying ? "pause icon" : "play-icon"}
          className={cn(
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform",
            controlsProps?.className,
          )}
        />
      )}
      {action && (
        <div
          className={cn(
            "absolute bottom-4 left-4 w-full min-w-0 max-w-[597px] space-y-1 sm:bottom-6 sm:left-6 md:space-y-3 lg:bottom-10 lg:left-10 rtl:bottom-4 rtl:right-4 md:rtl:bottom-6 rtl:md:right-6",
            action?.containerClassName,
          )}
        >
          {typeof action?.title === "string" ? (
            <div className="max-w-[387px] min-w-0">
              <h2 className={cn("break-words text-base font-bold text-white md:text-[32px]", action?.titleClassName)}>
                {action?.title}
              </h2>
            </div>
          ) : (
            action?.title
          )}
          {action?.description && (
            <div className="max-w-[377px] min-w-0 break-words text-xs text-white md:text-lg">
              {typeof action?.description === "string" ? (
                <p className="whitespace-normal break-words">{action?.description}</p>
              ) : (
                <PortableText value={action?.description as unknown as PortableTextBlock} />
              )}
            </div>
          )}
          {action?.subDescription && (
            <div className="mt-1 max-w-[377px] min-w-0 text-sm text-white md:text-base">
              <PortableText value={action?.subDescription as unknown as PortableTextBlock} />
            </div>
          )}
          {typeof action?.cta === "string" ? <p>{action?.cta}</p> : action?.cta}
        </div>
      )}
      {onVisible && (
        <div className="w-full items-center justify-center">
          <VisibilitySensor onVisible={onVisible}>
            <div />
          </VisibilitySensor>
        </div>
      )}
    </div>
  );
};

export default VideoCard;
